import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  where,
  limit,
  onSnapshot,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { startOfMonth, endOfMonth, format } from 'date-fns';

const useExpenseStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    expenses: [],
    recentExpenses: [],
    loading: false,
    error: null,
    filters: {
      category: null,
      priority: null,
      dateRange: null,
      paidBy: null,
      tags: null
    },
    unsubscribeExpenses: null,

    // Actions
    subscribeToExpenses: (familyId, filters = {}) => {
      if (!familyId) return;

      // Unsubscribe from previous subscription
      const { unsubscribeExpenses } = get();
      if (unsubscribeExpenses) {
        unsubscribeExpenses();
      }

      const expensesRef = collection(db, `families/${familyId}/expenses`);
      let q = query(expensesRef, orderBy('date', 'desc'));

      // Apply filters
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }
      if (filters.priority) {
        q = query(q, where('priority', '==', filters.priority));
      }
      if (filters.paidBy) {
        q = query(q, where('paidBy', '==', filters.paidBy));
      }
      if (filters.tags && filters.tags.length > 0) {
        q = query(q, where('tags', 'array-contains-any', filters.tags));
      }
      if (filters.dateRange) {
        q = query(
          q, 
          where('date', '>=', filters.dateRange.start),
          where('date', '<=', filters.dateRange.end)
        );
      }

      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const expenses = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          set({ 
            expenses, 
            loading: false, 
            error: null,
            unsubscribeExpenses: unsubscribe
          });
        },
        (error) => {
          console.error('Error subscribing to expenses:', error);
          set({ error: error.message, loading: false });
        }
      );

      set({ unsubscribeExpenses: unsubscribe, filters });
    },

    getRecentExpenses: async (familyId, limitCount = 10) => {
      if (!familyId) return { success: false, error: 'No family ID provided' };

      try {
        const expensesRef = collection(db, `families/${familyId}/expenses`);
        const q = query(expensesRef, orderBy('date', 'desc'), limit(limitCount));
        const snapshot = await getDocs(q);
        
        const recentExpenses = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        set({ recentExpenses });
        return { success: true, expenses: recentExpenses };
      } catch (error) {
        console.error('Error fetching recent expenses:', error);
        return { success: false, error: error.message };
      }
    },

    createExpense: async (familyId, userId, expenseData, receiptFile = null) => {
      if (!familyId || !userId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        let receiptUrl = null;

        // Upload receipt if provided
        if (receiptFile) {
          const timestamp = Date.now();
          const fileName = `receipt_${timestamp}_${receiptFile.name}`;
          const storageRef = ref(storage, `families/${familyId}/receipts/${fileName}`);
          
          const uploadResult = await uploadBytes(storageRef, receiptFile);
          receiptUrl = await getDownloadURL(uploadResult.ref);
        }

        const expensesRef = collection(db, `families/${familyId}/expenses`);
        const newExpense = {
          ...expenseData,
          familyId,
          receiptUrl,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: userId,
          date: expenseData.date || new Date()
        };

        const docRef = await addDoc(expensesRef, newExpense);
        
        // Update budget spending if budget exists for current month
        await get().updateBudgetSpending(familyId, newExpense.category, newExpense.amount);
        
        set({ loading: false, error: null });
        return { success: true, id: docRef.id };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    updateExpense: async (familyId, expenseId, updates, newReceiptFile = null, removeReceipt = false) => {
      if (!familyId || !expenseId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        // Get original expense data first
        const originalExpenseDoc = await getDoc(doc(db, `families/${familyId}/expenses`, expenseId));
        const originalExpense = originalExpenseDoc.data();
        
        let receiptUrl = originalExpense?.receiptUrl || null;

        // Handle receipt removal
        if (removeReceipt) {
          // Delete old receipt if exists
          if (originalExpense?.receiptUrl) {
            try {
              const oldReceiptRef = ref(storage, originalExpense.receiptUrl);
              await deleteObject(oldReceiptRef);
            } catch (error) {
              console.warn('Could not delete old receipt:', error);
            }
          }
          receiptUrl = null;
        }
        // Handle receipt upload/update
        else if (newReceiptFile) {
          // Delete old receipt if exists
          if (originalExpense?.receiptUrl) {
            try {
              const oldReceiptRef = ref(storage, originalExpense.receiptUrl);
              await deleteObject(oldReceiptRef);
            } catch (error) {
              console.warn('Could not delete old receipt:', error);
            }
          }

          // Upload new receipt
          const timestamp = Date.now();
          const fileName = `receipt_${timestamp}_${newReceiptFile.name}`;
          const storageRef = ref(storage, `families/${familyId}/receipts/${fileName}`);
          
          const uploadResult = await uploadBytes(storageRef, newReceiptFile);
          receiptUrl = await getDownloadURL(uploadResult.ref);
        }

        const expenseRef = doc(db, `families/${familyId}/expenses`, expenseId);
        const updateData = {
          ...updates,
          updatedAt: new Date()
        };
        
        // Handle receiptUrl - include it if it's not null, or explicitly set to null if removing
        if (removeReceipt || newReceiptFile) {
          updateData.receiptUrl = receiptUrl;
        }
        
        await updateDoc(expenseRef, updateData);

        // Update budget if category or amount changed
        if (originalExpense) {
          // Remove old amount from budget
          if (originalExpense.category && originalExpense.amount) {
            await get().updateBudgetSpending(familyId, originalExpense.category, originalExpense.amount, false);
          }
          
          // Add new amount to budget
          const newCategory = updates.category || originalExpense.category;
          const newAmount = updates.amount || originalExpense.amount;
          if (newCategory && newAmount) {
            await get().updateBudgetSpending(familyId, newCategory, newAmount, true);
          }
        }

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    deleteExpense: async (familyId, expenseId) => {
      if (!familyId || !expenseId) return { success: false, error: 'Missing required parameters' };

      set({ loading: true, error: null });
      try {
        // Get expense data to check for receipt
        const expenseRef = doc(db, `families/${familyId}/expenses`, expenseId);
        const expenseDoc = await getDoc(expenseRef);
        
        if (expenseDoc.exists()) {
          const expenseData = expenseDoc.data();
          
          // Delete receipt if exists
          if (expenseData.receiptUrl) {
            try {
              const receiptRef = ref(storage, expenseData.receiptUrl);
              await deleteObject(receiptRef);
            } catch (error) {
              console.warn('Could not delete receipt:', error);
            }
          }

          // Update budget by removing this expense amount
          if (expenseData.category && expenseData.amount) {
            await get().updateBudgetSpending(familyId, expenseData.category, expenseData.amount, false);
          }
        }

        // Delete expense document
        await deleteDoc(expenseRef);

        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    getExpenseById: async (familyId, expenseId) => {
      if (!familyId || !expenseId) return { success: false, error: 'Missing required parameters' };

      try {
        const expenseRef = doc(db, `families/${familyId}/expenses`, expenseId);
        const expenseDoc = await getDoc(expenseRef);
        
        if (expenseDoc.exists()) {
          return { 
            success: true, 
            expense: { id: expenseDoc.id, ...expenseDoc.data() } 
          };
        } else {
          return { success: false, error: 'Expense not found' };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    getExpensesByCategory: (categoryId) => {
      const { expenses } = get();
      return expenses.filter(expense => expense.category === categoryId);
    },

    getExpensesByPriority: (priority) => {
      const { expenses } = get();
      return expenses.filter(expense => expense.priority === priority);
    },

    getExpensesByDateRange: (startDate, endDate) => {
      const { expenses } = get();
      return expenses.filter(expense => {
        const expenseDate = expense.date.toDate ? expense.date.toDate() : new Date(expense.date);
        return expenseDate >= startDate && expenseDate <= endDate;
      });
    },

    getCurrentMonthExpenses: () => {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      return get().getExpensesByDateRange(monthStart, monthEnd);
    },

    getTotalExpenses: (expensesList = null) => {
      const expenses = expensesList || get().expenses;
      return expenses.reduce((total, expense) => total + (expense.amount || 0), 0);
    },

    getExpensesByUser: (userId) => {
      const { expenses } = get();
      return expenses.filter(expense => expense.paidBy === userId);
    },

    getExpensesByTags: (tagIds) => {
      const { expenses } = get();
      if (!tagIds || tagIds.length === 0) return expenses;
      return expenses.filter(expense => 
        expense.tags && expense.tags.some(tagId => tagIds.includes(tagId))
      );
    },

    updateFilters: (newFilters) => {
      set(state => ({
        filters: { ...state.filters, ...newFilters }
      }));
    },

    clearFilters: () => {
      set({
        filters: {
          category: null,
          priority: null,
          dateRange: null,
          paidBy: null,
          tags: null
        }
      });
    },

    // Update budget spending when expense is added/updated/deleted
    updateBudgetSpending: async (familyId, categoryId, amount, isAddition = true) => {
      try {
        // Get current month budget
        const currentMonth = format(new Date(), 'yyyy-MM');
        const budgetsRef = collection(db, `families/${familyId}/budgets`);
        const budgetQuery = query(budgetsRef, where('month', '==', currentMonth));
        const budgetSnapshot = await getDocs(budgetQuery);
        
        if (budgetSnapshot.empty) {
          console.log('No budget found for current month, skipping budget update');
          return;
        }

        const budgetDoc = budgetSnapshot.docs[0];
        const budgetData = budgetDoc.data();
        const categories = { ...budgetData.categories };
        
        // Initialize category if it doesn't exist in budget
        if (!categories[categoryId]) {
          categories[categoryId] = { 
            budgeted: 0, 
            spent: 0, 
            priority: 'need', 
            transactions: 0 
          };
        }

        // Update category spending
        const currentSpent = categories[categoryId].spent || 0;
        const currentTransactions = categories[categoryId].transactions || 0;
        
        categories[categoryId].spent = isAddition 
          ? currentSpent + amount 
          : Math.max(0, currentSpent - amount);
        categories[categoryId].transactions = isAddition 
          ? currentTransactions + 1 
          : Math.max(0, currentTransactions - 1);

        // Calculate new total spent
        const totalSpent = Object.values(categories).reduce((sum, cat) => sum + (cat.spent || 0), 0);

        // Update budget document
        await updateDoc(budgetDoc.ref, {
          categories,
          totalSpent,
          updatedAt: new Date()
        });

        console.log('Budget updated successfully for category:', categoryId, 'amount:', amount);
      } catch (error) {
        console.error('Error updating budget spending:', error);
      }
    },

    clearError: () => set({ error: null }),

    cleanup: () => {
      const { unsubscribeExpenses } = get();
      if (unsubscribeExpenses) {
        unsubscribeExpenses();
        set({ unsubscribeExpenses: null });
      }
    }
  }))
);

export default useExpenseStore;

